import { useWizardStore } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface Step9AuthProps { lang: Lang }

const COPY = {
  fr: {
    title: 'Identifiez-vous pour sauvegarder votre configuration',
    body: 'Votre configuration sera liée à votre compte robot.fric.link. Vous pourrez la retrouver, la modifier et générer votre cahier des charges à tout moment.',
    loginBtn: 'Se connecter avec robot.fric.link',
    note: 'Connexion sécurisée via Entra External ID. Aucun mot de passe stocké.',
    orContinue: 'ou continuer sans compte (non sauvegardé)',
    loggedIn: 'Authentifié',
    loggedInBody: 'Vous êtes connecté. Votre configuration sera sauvegardée automatiquement.',
    continueAnon: 'Continuer sans sauvegarder',
  },
  en: {
    title: 'Sign in to save your configuration',
    body: 'Your configuration will be linked to your robot.fric.link account. You can retrieve, edit and generate your specifications sheet at any time.',
    loginBtn: 'Sign in with robot.fric.link',
    note: 'Secure login via Entra External ID. No password stored.',
    orContinue: 'or continue without account (unsaved)',
    loggedIn: 'Authenticated',
    loggedInBody: 'You are signed in. Your configuration will be saved automatically.',
    continueAnon: 'Continue without saving',
  },
}

export default function Step9Auth({ lang }: Step9AuthProps) {
  const { isAuthenticated, setAuthenticated, goNext } = useWizardStore()
  const t = COPY[lang]

  if (isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-8 text-center max-w-lg mx-auto">
        <div className="w-20 h-20 rounded-full bg-primary-container/20 flex items-center justify-center">
          <span className="material-symbols-outlined text-5xl text-primary-container">verified_user</span>
        </div>
        <div>
          <h2 className="font-headline text-2xl font-bold text-on-surface mb-3">{t.loggedIn}</h2>
          <p className="text-on-surface-variant font-body text-sm leading-relaxed">{t.loggedInBody}</p>
        </div>
        <div className="flex items-center gap-2 text-xs font-label tracking-widest uppercase text-primary-container">
          <div className="w-2 h-2 bg-primary-container rounded-full blur-[1px] animate-pulse" />
          robot.fric.link
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 gap-10 text-center max-w-lg mx-auto">
      <div className="w-20 h-20 rounded-full bg-surface-container-highest flex items-center justify-center">
        <span className="material-symbols-outlined text-5xl text-primary">memory</span>
      </div>

      <div>
        <h2 className="font-headline text-2xl font-bold text-on-surface mb-4">{t.title}</h2>
        <p className="text-on-surface-variant font-body text-sm leading-relaxed max-w-sm mx-auto">{t.body}</p>
      </div>

      <div className="flex flex-col items-center gap-4 w-full max-w-xs">
        {/* Triggers MSAL redirect — wired when SPA app registration is configured */}
        <button
          onClick={() => setAuthenticated(true)}
          className="btn-primary w-full text-base justify-center"
        >
          <span className="material-symbols-outlined text-xl">login</span>
          {t.loginBtn}
        </button>

        <p className="text-[10px] text-on-surface-variant font-label tracking-wide">{t.note}</p>

        <button
          onClick={() => { setAuthenticated(false); goNext() }}
          className="text-on-surface-variant hover:text-on-surface text-xs font-label tracking-wide transition-colors underline underline-offset-2"
        >
          {t.orContinue}
        </button>
      </div>
    </div>
  )
}
